<div class="tree-controls">
  @if (!embeddedMode()) {
    <input
      #searchInput
      type="search"
      class="tree-search"
      [class.tree-search--invalid]="searchRegexInvalid()"
      placeholder="Find keys or values..."
      i18n-placeholder="@@tree.search.placeholder"
      aria-label="Find in tree"
      i18n-aria-label="@@tree.search.aria"
      [value]="search()"
      (input)="onSearchInput($event)"
      (keydown.escape)="onSearchEsc($event)"
      (keydown.enter)="onSearchEnter($event)"
      (keydown.shift.enter)="onSearchEnter($event)"
      (keydown.alt.r)="onMatchModeShortcut($event)"
    />
    <button
      type="button"
      class="tree-select tree-search-toggle"
      [class.is-active]="searchCaseSensitive()"
      [attr.aria-pressed]="searchCaseSensitive()"
      [title]="searchCaseSensitiveTooltip"
      [attr.aria-label]="searchCaseSensitiveTooltip"
      (click)="toggleSearchCaseSensitive()"
    >
      {{ searchCaseSensitiveLabel }}
    </button>
    <button
      type="button"
      class="tree-select tree-search-matchmode"
      [class.is-active]="searchMatchMode() !== 'contains'"
      [matMenuTriggerFor]="matchModeMenu"
      [title]="searchMatchModeTooltip"
      [attr.aria-label]="searchMatchModeTooltip"
      aria-haspopup="menu"
    >
      {{ searchMatchModeButtonLabel() }}
    </button>
    <mat-menu #matchModeMenu="matMenu" class="jj-menu">
      @for (item of matchModes; track item.mode) {
        <button
          type="button"
          mat-menu-item
          class="jj-menu-item--radio"
          [class.is-checked]="searchMatchMode() === item.mode"
          [attr.aria-checked]="searchMatchMode() === item.mode"
          role="menuitemradio"
          (click)="setSearchMatchMode(item.mode)"
        >
          <jj-icon class="jj-menu-item__check" name="check" [size]="18" aria-hidden="true" />
          <span>{{ item.label() }}</span>
        </button>
      }
    </mat-menu>
    <button
      type="button"
      class="tree-select tree-search-scope"
      [matMenuTriggerFor]="scopeMenu"
      [title]="searchScopeTooltip"
      [attr.aria-label]="searchScopeTooltip"
      aria-haspopup="menu"
    >
      {{ searchScopeButtonLabel() }}
    </button>
    <mat-menu #scopeMenu="matMenu" class="jj-menu">
      <button
        type="button"
        mat-menu-item
        class="jj-menu-item--radio"
        [class.is-checked]="searchScope() === 'keys'"
        [attr.aria-checked]="searchScope() === 'keys'"
        role="menuitemradio"
        (click)="setSearchScope('keys')"
      >
        <jj-icon class="jj-menu-item__check" name="check" [size]="18" aria-hidden="true" />
        <span>{{ searchScopeKeysLabel }}</span>
      </button>
      <button
        type="button"
        mat-menu-item
        class="jj-menu-item--radio"
        [class.is-checked]="searchScope() === 'values'"
        [attr.aria-checked]="searchScope() === 'values'"
        role="menuitemradio"
        (click)="setSearchScope('values')"
      >
        <jj-icon class="jj-menu-item__check" name="check" [size]="18" aria-hidden="true" />
        <span>{{ searchScopeValuesLabel }}</span>
      </button>
      <button
        type="button"
        mat-menu-item
        class="jj-menu-item--radio"
        [class.is-checked]="searchScope() === 'both'"
        [attr.aria-checked]="searchScope() === 'both'"
        role="menuitemradio"
        (click)="setSearchScope('both')"
      >
        <jj-icon class="jj-menu-item__check" name="check" [size]="18" aria-hidden="true" />
        <span>{{ searchScopeBothLabel }}</span>
      </button>
    </mat-menu>
    <button
      type="button"
      class="tree-select tree-search-type"
      [class.is-active]="searchValueType() !== 'all'"
      [matMenuTriggerFor]="typeMenu"
      [title]="searchValueTypeTooltip"
      [attr.aria-label]="searchValueTypeTooltip"
      aria-haspopup="menu"
    >
      {{ searchValueTypeButtonLabel() }}
    </button>
    <mat-menu #typeMenu="matMenu" class="jj-menu">
      @for (t of searchValueTypes; track t) {
        <button
          type="button"
          mat-menu-item
          class="jj-menu-item--radio"
          [class.is-checked]="searchValueType() === t"
          [attr.aria-checked]="searchValueType() === t"
          role="menuitemradio"
          (click)="setSearchValueType(t)"
        >
          <jj-icon class="jj-menu-item__check" name="check" [size]="18" aria-hidden="true" />
          <span>{{ valueTypeLabel(t) }}</span>
        </button>
      }
    </mat-menu>
    <button
      type="button"
      class="tree-btn tree-search-nav"
      [disabled]="searchPrevDisabled()"
      [title]="searchPrevTooltip"
      [attr.aria-label]="searchPrevTooltip"
      (click)="goToPrevMatch()"
    >
      &lsaquo;
    </button>
    <button
      type="button"
      class="tree-btn tree-search-nav"
      [disabled]="searchNextDisabled()"
      [title]="searchNextTooltip"
      [attr.aria-label]="searchNextTooltip"
      (click)="goToNextMatch()"
    >
      &rsaquo;
    </button>
    @if (searchCountLabel()) {
      <span class="tree-search-count">
        <span class="tree-search-count__live" aria-live="polite">{{ searchCountLabel() }}</span>
        @if (searchCountGhost()) {
          <span class="tree-search-count__ghost" aria-hidden="true">{{ searchCountGhost() }}</span>
        }
      </span>
    }
  }
  <button
    type="button"
    class="tree-select"
    [matMenuTriggerFor]="expandMenu"
    [attr.aria-label]="expandMenuButtonLabel"
  >
    {{ expandMenuButtonLabel }}
  </button>
  <mat-menu #expandMenu="matMenu" class="jj-menu">
    <button type="button" mat-menu-item (click)="expandToLevel(1)" i18n="@@tree.expand.level.1"
      >Expand: 1</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(2)" i18n="@@tree.expand.level.2"
      >Expand: 2</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(3)" i18n="@@tree.expand.level.3"
      >Expand: 3</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(4)" i18n="@@tree.expand.level.4"
      >Expand: 4</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(5)" i18n="@@tree.expand.level.5"
      >Expand: 5</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(6)" i18n="@@tree.expand.level.6"
      >Expand: 6</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(7)" i18n="@@tree.expand.level.7"
      >Expand: 7</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(8)" i18n="@@tree.expand.level.8"
      >Expand: 8</button
    >
    <button type="button" mat-menu-item (click)="expandToLevel(9)" i18n="@@tree.expand.level.9"
      >Expand: 9</button
    >
    <button type="button" mat-menu-item (click)="expandAll()" i18n="@@tree.expand.level.all"
      >Expand: all</button
    >
  </mat-menu>
  <button type="button" class="tree-btn" (click)="expandAll()" i18n="@@tree.expand.all">
    Expand all
  </button>
  <button type="button" class="tree-btn" (click)="collapseAll()" i18n="@@tree.collapse.all">
    Collapse all
  </button>
</div>

@if (root()) {
  <jj-breadcrumb
    class="tree-breadcrumb"
    [crumbs]="crumbs()"
    [emptyPlaceholder]="breadcrumbEmptyLabel"
    [navAriaLabel]="breadcrumbAriaLabel"
    [overflowAriaLabel]="breadcrumbOverflowAriaLabel"
    [copyPathTitle]="breadcrumbCopyPathTitle"
    [copyPathAriaLabel]="breadcrumbCopyPathAriaLabel"
    [copyPathDisabled]="breadcrumbCopyDisabled()"
    (crumbClick)="onBreadcrumbClick($event)"
    (crumbContextMenu)="onBreadcrumbContextMenu($event)"
    (copyPathClick)="onBreadcrumbCopyPath()"
  />
}

<div
  class="tree-body"
  [style.--tree-font-size]="treeFontSizePx()"
  [style.--tree-row-height.px]="effectiveRowHeightPx()"
>
  <!--
    Offscreen probe row used to measure rendered row height for both
    the auto-fit-initial-expansion algorithm and the virtual scroll
    viewport's [itemSize] binding (issue #95 Phase 2). The probe
    carries every height-driving element across leaf AND open rows
    (twisty, beacon-badge, key+value rule icons, key, value-string,
    value-container brace, date-annotation, leading+trailing
    comments, right-cluster pills) so the measured height is an
    upper bound across any row that may render. Issue #269: probe
    mirrors the Grid wrapper structure (`.tree-row-leading`,
    `.tree-row-value-cell`, `.tree-row-trailing`); probe uses
    `<span>` everywhere (inert) so no implicit button styling
    pollutes the height measurement.
  -->
  <!-- Structural invariants enforced by scripts/check-tree-row-grid.mjs -->
  <div #rowHeightProbe class="tree-row tree-row--probe" aria-hidden="true">
    <span class="tree-row-leading">
      <span class="tree-twisty" tabindex="-1">
        <jj-icon name="chevron-right" [size]="'auto'" />
      </span>
      <span class="tree-beacon-badge" tabindex="-1">
        <jj-icon name="check" [size]="'auto'" />
      </span>
      <span class="tree-comment-slot tree-comment-slot--leading">
        <span class="tree-comment tree-comment-leading">// c</span>
        <span class="tree-comment-count" dir="ltr">(+99)</span>
      </span>
      <jj-icon class="tree-rule-icon tree-rule-icon--key" name="check" [size]="'auto'" />
    </span>
    <!-- intentionally raw: row-height probe; do not wrap in displayKey -->
    <span class="tree-key">probe</span>
    <span class="tree-key-sep">: </span>
    <span class="tree-row-value-cell">
      <jj-icon class="tree-rule-icon tree-rule-icon--value" name="check" [size]="'auto'" />
      <span class="tree-value-string">"x"</span>
      <span class="tree-value-container">
        <span class="tree-value-brace">&#123;</span>
      </span>
    </span>
    <span class="tree-row-trailing">
      <span class="tree-date-annotation">&nbsp;(2024)</span>
      <span class="tree-comment-slot tree-comment-slot--trailing">
        <span class="tree-comment tree-comment-trailing">// t</span>
      </span>
    </span>
    <span class="tree-row-right">
      <span class="tree-count">(0)</span>
      <span class="tree-type-badge">string</span>
      <span class="tree-extract-pill" tabindex="-1">
        <jj-icon name="extract" [size]="'auto'" />
      </span>
      <span class="tree-decoded-pill" tabindex="-1">
        <jj-icon name="decoded" [size]="'auto'" />
      </span>
      <span class="tree-kebab-pill" tabindex="-1">
        <jj-icon name="more-vert" [size]="'auto'" />
      </span>
    </span>
  </div>
  @if (root()) {
    <cdk-virtual-scroll-viewport
      #viewport
      class="tree-viewport"
      [itemSize]="effectiveRowHeightPx()"
      [attr.aria-label]="treeAriaLabel"
      role="tree"
    >
      @if (flatList().length > 0) {
        <ng-container *cdkVirtualFor="let item of flatList(); trackBy: trackByFlatItem">
          @switch (item.kind) {
            @case ('leaf') {
              @let leafNode = item.node;
              @let manualHighlight = resolvedHighlightsByPath().get(leafNode.pathString);
              <div
                class="tree-row jj-no-select"
                role="treeitem"
                [attr.data-path]="leafNode.pathString"
                [attr.aria-level]="nodeAriaLevel(leafNode)"
                [attr.aria-posinset]="nodePosInSet(leafNode)"
                [attr.aria-setsize]="nodeSetSize(leafNode)"
                [attr.aria-selected]="selectedPath() === leafNode.pathString ? 'true' : null"
                [attr.tabindex]="rowTabIndex(leafNode)"
                [style]="ruleStyleVars(leafNode)"
                [style.--manual-highlight]="manualHighlight?.color ?? null"
                [style.padding-left.em]="item.level * 1.25"
                [attr.title]="matchedRuleTitle(leafNode)"
                [class.has-manual-highlight]="manualHighlight"
                [class.is-search-hit]="searchHits().has(leafNode.pathString)"
                [class.is-active-match]="activeHitPath() === leafNode.pathString"
                [class.is-ancestor]="ancestorPaths().has(leafNode.pathString)"
                [class.is-match]="matchingPaths().has(leafNode.pathString)"
                [class.is-selected]="selectedPath() === leafNode.pathString"
                (click)="onSelect(leafNode, $event)"
                (contextmenu)="onRowContextMenu($event, leafNode)"
                (dblclick)="onRowDblClick($event, leafNode)"
                (keydown)="onTreeKeydown($event, leafNode)"
              >
                <span class="tree-row-leading">
                  <span class="tree-twisty tree-spacer" aria-hidden="true">&middot;</span>

                  @if (showComments() && leadingComment(leafNode); as lc) {
                    <span class="tree-comment-slot tree-comment-slot--leading">
                      <span
                        class="tree-comment tree-comment-leading"
                        [matTooltip]="leadingCommentTooltipPrefix + lc.tooltipBody"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ lc.visible }}</span
                      >
                      @if (lc.count >= 2) {
                        <span
                          class="tree-comment-count"
                          dir="ltr"
                          [attr.aria-label]="moreBadgeAriaLabel(lc.count - 1)"
                          >{{ moreBadge(lc.count - 1) }}</span
                        >
                      }
                    </span>
                  }

                  @for (ki of keyIcons(leafNode); track ki) {
                    <jj-icon
                      class="tree-rule-icon tree-rule-icon--key"
                      [name]="ki"
                      [size]="'auto'"
                    />
                  }
                </span>
                @if (leafNode.segment !== undefined) {
                  @if (segmentIsIndex(leafNode)) {
                    <span class="tree-index">{{ leafNode.segment }}:</span>
                  } @else {
                    @let rawKey = leafNode.segment;
                    @let displayedKey = displayKey(rawKey);
                    <!-- title null when displayedKey === rawKey so row-level
                         matchedRuleTitle cascade applies; title set to raw
                         decoded form only when the key was transformed. -->
                    <span class="tree-key" [attr.title]="displayedKey === rawKey ? null : rawKey">{{
                      displayedKey
                    }}</span>
                    <span class="tree-key-sep">: </span>
                  }
                }

                <span class="tree-row-value-cell">
                  @for (vi of valueIcons(leafNode); track vi) {
                    <jj-icon
                      class="tree-rule-icon tree-rule-icon--value"
                      [name]="vi"
                      [size]="'auto'"
                    />
                  }
                  @switch (leafNode.type) {
                    @case ('string') {
                      @let leafText = displayLeaf(leafNode);
                      <span
                        class="tree-value-string"
                        [jjOverflowDetector]="leafText"
                        #leafOverflow="jjOverflowDetector"
                        [matTooltip]="clampTooltip(leafText)"
                        [matTooltipDisabled]="!leafOverflow.overflowing()"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ leafText }}</span
                      >
                    }
                    @case ('number') {
                      <span class="tree-value-number">{{
                        renderLeaf(leafNode.value, leafNode.type)
                      }}</span>
                    }
                    @case ('boolean') {
                      <span class="tree-value-boolean">{{
                        renderLeaf(leafNode.value, leafNode.type)
                      }}</span>
                    }
                    @case ('null') {
                      <span class="tree-value-null">null</span>
                    }
                    @case ('array') {
                      <span class="tree-value-container">
                        <span class="tree-value-brace">[]</span>
                      </span>
                    }
                    @case ('object') {
                      <span class="tree-value-container">
                        <span class="tree-value-brace">&#123;&#125;</span>
                      </span>
                    }
                  }
                </span>

                <span class="tree-row-trailing">
                  @if (leafNode.type === 'string' && showDateAnnotations()) {
                    @let ann = dateAnnotation(leafNode);
                    @if (ann) {
                      <span
                        class="tree-date-annotation"
                        aria-label="Date annotation"
                        i18n-aria-label="@@tree.dateAnnotation.aria"
                        >&nbsp;({{ ann }})</span
                      >
                    }
                  }
                  @if (showComments() && trailingComment(leafNode); as tc) {
                    <span class="tree-comment-slot tree-comment-slot--trailing">
                      <span
                        class="tree-comment tree-comment-trailing"
                        [matTooltip]="trailingCommentTooltipPrefix + tc.tooltipBody"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ tc.visible }}</span
                      >
                      @if (tc.count >= 2) {
                        <span
                          class="tree-comment-count"
                          dir="ltr"
                          [attr.aria-label]="moreBadgeAriaLabel(tc.count - 1)"
                          >{{ moreBadge(tc.count - 1) }}</span
                        >
                      }
                    </span>
                  }
                </span>

                <span class="tree-row-right">
                  @if (matchingPaths().has(leafNode.pathString)) {
                    <span
                      class="tree-match-badge"
                      [attr.aria-label]="matchingValueAriaLabel"
                      i18n-aria-label="@@tree.matchValue.aria"
                      >&middot;</span
                    >
                  }
                  @if (leafNode.type === 'array' || leafNode.type === 'object') {
                    <span class="tree-count">{{ containerCountText(leafNode) }}</span>
                  }
                  @if (showTypeBadges()) {
                    <span class="tree-type-badge">{{ typeLabel(leafNode) }}</span>
                  }
                  @if (extractCandidate(leafNode) && !decodedCandidate(leafNode)) {
                    <button
                      type="button"
                      class="tree-extract-pill"
                      (click)="onExtractButtonClick(leafNode, $event)"
                      i18n-title="@@tree.extract.button.title"
                      title="Extract embedded JSON"
                      i18n-aria-label="@@tree.extract.button.aria"
                      aria-label="Extract embedded JSON from this string"
                    >
                      <jj-icon name="extract" [size]="'auto'" />
                    </button>
                  }
                  @if (decodedCandidate(leafNode)) {
                    <button
                      type="button"
                      class="tree-decoded-pill"
                      [attr.title]="decodedPillTitleFor(leafNode)"
                      [attr.aria-label]="decodedPillAriaFor(leafNode)"
                      (click)="onDecodedButtonClick(leafNode, $event)"
                    >
                      <jj-icon name="decoded" [size]="'auto'" />
                    </button>
                  }
                  <button
                    type="button"
                    class="tree-kebab-pill"
                    [matMenuTriggerFor]="rowMenu"
                    (click)="onKebabClick($event, leafNode)"
                    [attr.aria-label]="kebabAriaLabel"
                    [attr.title]="kebabTitleLabel"
                  >
                    <jj-icon name="more-vert" [size]="'auto'" />
                  </button>
                </span>
                @if (manualHighlight) {
                  <span class="sr-only" i18n="@@tree.highlighted.aria">highlighted</span>
                }
              </div>
            }
            @case ('open') {
              @let openNode = item.node;
              @let openManualHighlight = resolvedHighlightsByPath().get(openNode.pathString);
              <div
                class="tree-row jj-no-select"
                role="treeitem"
                [attr.data-path]="openNode.pathString"
                [attr.aria-level]="nodeAriaLevel(openNode)"
                [attr.aria-posinset]="nodePosInSet(openNode)"
                [attr.aria-setsize]="nodeSetSize(openNode)"
                [attr.aria-expanded]="isExpanded(openNode)"
                [attr.aria-selected]="selectedPath() === openNode.pathString ? 'true' : null"
                [attr.tabindex]="rowTabIndex(openNode)"
                [style]="ruleStyleVars(openNode)"
                [style.--manual-highlight]="openManualHighlight?.color ?? null"
                [style.padding-left.em]="item.level * 1.25"
                [attr.title]="matchedRuleTitle(openNode)"
                [class.has-manual-highlight]="openManualHighlight"
                [class.is-search-hit]="searchHits().has(openNode.pathString)"
                [class.is-active-match]="activeHitPath() === openNode.pathString"
                [class.is-ancestor]="ancestorPaths().has(openNode.pathString)"
                [class.is-match]="matchingPaths().has(openNode.pathString)"
                [class.is-selected]="selectedPath() === openNode.pathString"
                (click)="onSelect(openNode, $event)"
                (contextmenu)="onRowContextMenu($event, openNode)"
                (dblclick)="onRowDblClick($event, openNode)"
                (keydown)="onTreeKeydown($event, openNode)"
              >
                <span class="tree-row-leading">
                  <button
                    type="button"
                    class="tree-twisty"
                    (click)="onTwistyClick(openNode, $event)"
                    [attr.aria-label]="isExpanded(openNode) ? collapseLabel : expandLabel"
                  >
                    <jj-icon
                      [name]="isExpanded(openNode) ? 'chevron-down' : 'chevron-right'"
                      [size]="'auto'"
                    />
                  </button>

                  @for (ai of ancestorBeaconIcons(openNode); track ai) {
                    <button
                      type="button"
                      class="tree-beacon-badge"
                      [attr.aria-label]="ancestorBadgeTooltip(openNode, ai)"
                      [matTooltip]="ancestorBadgeTooltip(openNode, ai)"
                      matTooltipShowDelay="500"
                      (click)="onAncestorBadgeClick(openNode, ai, $event)"
                    >
                      <jj-icon [name]="ai" [size]="'auto'" />
                    </button>
                  }

                  @if (showComments() && leadingComment(openNode); as lc) {
                    <span class="tree-comment-slot tree-comment-slot--leading">
                      <span
                        class="tree-comment tree-comment-leading"
                        [matTooltip]="leadingCommentTooltipPrefix + lc.tooltipBody"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ lc.visible }}</span
                      >
                      @if (lc.count >= 2) {
                        <span
                          class="tree-comment-count"
                          dir="ltr"
                          [attr.aria-label]="moreBadgeAriaLabel(lc.count - 1)"
                          >{{ moreBadge(lc.count - 1) }}</span
                        >
                      }
                    </span>
                  }

                  @for (ki of keyIcons(openNode); track ki) {
                    <jj-icon
                      class="tree-rule-icon tree-rule-icon--key"
                      [name]="ki"
                      [size]="'auto'"
                    />
                  }
                </span>
                @if (openNode.segment !== undefined) {
                  @if (segmentIsIndex(openNode)) {
                    <span class="tree-index">{{ openNode.segment }}:</span>
                  } @else {
                    @let openRawKey = openNode.segment;
                    @let openDisplayedKey = displayKey(openRawKey);
                    <!-- title null when openDisplayedKey === openRawKey so
                         row-level matchedRuleTitle cascade applies; title
                         set to raw decoded form only when transformed. -->
                    <span
                      class="tree-key"
                      [attr.title]="openDisplayedKey === openRawKey ? null : openRawKey"
                      >{{ openDisplayedKey }}</span
                    >
                    <span class="tree-key-sep">: </span>
                  }
                }

                <span class="tree-row-value-cell">
                  <span class="tree-value-container">
                    <span class="tree-value-brace">
                      {{
                        isExpanded(openNode)
                          ? openNode.type === 'array'
                            ? '['
                            : '{'
                          : containerSummary(openNode)
                      }}
                    </span>
                  </span>
                </span>

                <span class="tree-row-trailing">
                  @if (showComments() && trailingComment(openNode); as tc) {
                    <span class="tree-comment-slot tree-comment-slot--trailing">
                      <span
                        class="tree-comment tree-comment-trailing"
                        [matTooltip]="trailingCommentTooltipPrefix + tc.tooltipBody"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ tc.visible }}</span
                      >
                      @if (tc.count >= 2) {
                        <span
                          class="tree-comment-count"
                          dir="ltr"
                          [attr.aria-label]="moreBadgeAriaLabel(tc.count - 1)"
                          >{{ moreBadge(tc.count - 1) }}</span
                        >
                      }
                    </span>
                  }
                </span>

                <span class="tree-row-right">
                  <span class="tree-count">{{ containerCountText(openNode) }}</span>
                  @if (showTypeBadges()) {
                    <span class="tree-type-badge">{{ typeLabel(openNode) }}</span>
                  }
                  <button
                    type="button"
                    class="tree-kebab-pill"
                    [matMenuTriggerFor]="rowMenu"
                    (click)="onKebabClick($event, openNode)"
                    [attr.aria-label]="kebabAriaLabel"
                    [attr.title]="kebabTitleLabel"
                  >
                    <jj-icon name="more-vert" [size]="'auto'" />
                  </button>
                </span>
                @if (openManualHighlight) {
                  <span class="sr-only" i18n="@@tree.highlighted.aria">highlighted</span>
                }
              </div>
            }
            @case ('close') {
              @let closeNode = item.node;
              @let closeManualHighlight = cascadeHighlightsByPath().get(closeNode.pathString);
              <div
                class="tree-row tree-row--close"
                role="presentation"
                [attr.data-close-path]="closeNode.pathString"
                [style.padding-left.em]="item.level * 1.25"
                [class.has-manual-highlight]="closeManualHighlight"
                [style.--manual-highlight]="closeManualHighlight?.color ?? null"
                (contextmenu)="onCloseRowContextMenu($event, closeNode)"
              >
                <span class="tree-row-leading">
                  <span class="tree-twisty tree-spacer" aria-hidden="true">&middot;</span>

                  @if (showComments() && closeLeadingComment(closeNode); as cl) {
                    <span class="tree-comment-slot tree-comment-slot--leading-close">
                      <span
                        class="tree-comment tree-comment-leading tree-comment-leading--close"
                        [matTooltip]="closeLeadingCommentTooltipPrefix + cl.tooltipBody"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ cl.visible }}</span
                      >
                      @if (cl.count >= 2) {
                        <span
                          class="tree-comment-count"
                          dir="ltr"
                          [attr.aria-label]="moreBadgeAriaLabel(cl.count - 1)"
                          >{{ moreBadge(cl.count - 1) }}</span
                        >
                      }
                    </span>
                  }
                </span>
                <span class="tree-row-value-cell">
                  <span class="tree-value-brace">{{ closeNode.type === 'array' ? ']' : '}' }}</span>
                </span>
                <span class="tree-row-trailing">
                  @if (showComments() && closeTrailingComment(closeNode); as tc) {
                    <span class="tree-comment-slot tree-comment-slot--trailing-close">
                      <span
                        class="tree-comment tree-comment-trailing tree-comment-trailing--close"
                        [matTooltip]="closeTrailingCommentTooltipPrefix + tc.tooltipBody"
                        matTooltipShowDelay="500"
                        matTooltipPosition="above"
                        matTooltipClass="jj-tooltip-wide"
                        >{{ tc.visible }}</span
                      >
                      @if (tc.count >= 2) {
                        <span
                          class="tree-comment-count"
                          dir="ltr"
                          [attr.aria-label]="moreBadgeAriaLabel(tc.count - 1)"
                          >{{ moreBadge(tc.count - 1) }}</span
                        >
                      }
                    </span>
                  }
                </span>
                @if (closeManualHighlight) {
                  <span class="sr-only" i18n="@@tree.highlighted.aria">highlighted</span>
                }
              </div>
            }
          }
        </ng-container>
      }
    </cdk-virtual-scroll-viewport>
  } @else {
    <div class="tree-empty" i18n="@@tree.empty">
      Paste or type JSON on the left to see the tree view.
    </div>
  }
</div>

<!--
  Hidden positional anchor for cursor-driven right-click. Set
  `ctxX/ctxY` and call `ctxTrigger.openMenu()` to open `rowMenu`
  at the cursor. The kebab buttons use the same `rowMenu` directly
  via `[matMenuTriggerFor]`, anchoring at their own location.
-->
<button
  #ctxTrigger="matMenuTrigger"
  [matMenuTriggerFor]="rowMenu"
  [style.left.px]="ctxX()"
  [style.top.px]="ctxY()"
  class="tree-ctx-anchor"
  type="button"
  aria-hidden="true"
  tabindex="-1"
></button>

<mat-menu #rowMenu="matMenu" class="jj-menu tree-row-menu">
  @if (contextNode(); as cn) {
    <!-- Copy section -->
    <button
      mat-menu-item
      type="button"
      [class.ctx-default-action]="defaultActionKind() === 'copyValue'"
      (click)="copyValue(cn, 'menu', $event.altKey)"
    >
      <jj-icon name="copy" [size]="18" />
      <span>{{ ctxCopyValueLabel }}</span>
      @if (defaultActionKind() === 'copyValue') {
        <span class="sr-only">{{ defaultActionA11yHint }}</span>
      }
    </button>
    @if (showCopyKey(cn)) {
      <button mat-menu-item type="button" (click)="copyKey(cn)">
        <jj-icon name="key" [size]="18" />
        <span>{{ ctxCopyKeyLabel }}</span>
      </button>
    }
    <button mat-menu-item type="button" (click)="copyPathFromMenu(cn)">
      <jj-icon name="copy-path" [size]="18" />
      <span>{{ ctxCopyPathLabel }}</span>
    </button>
    <!-- Transform section: Extract / Decode (conditional). Existing
         icons preserved here; Phase 3 adds icons elsewhere. -->
    @if (extractCandidate(cn) || decodedCandidate(cn) || sortKeysCandidate(cn)) {
      <mat-divider />
    }
    @if (extractCandidate(cn)) {
      <button mat-menu-item type="button" (click)="onExtractMenuClick(cn)">
        <jj-icon name="extract" [size]="18" />
        <span i18n="@@tree.extract.menu.label">Extract embedded JSON</span>
      </button>
    }
    @if (decodedCandidate(cn)) {
      <button mat-menu-item type="button" (click)="onDecodedMenuClick(cn)">
        <jj-icon name="decoded" [size]="18" />
        <span>{{ decodedOpenDialogMenuLabel }}</span>
      </button>
    }
    @if (sortKeysCandidate(cn)) {
      <button mat-menu-item type="button" (click)="onSortKeysMenuClick(cn)">
        <jj-icon name="sort" [size]="18" />
        <span i18n="@@tree.sortKeys.menu.label">Sort keys</span>
      </button>
    }
    <!-- Mark section: single-row Highlight + Remove highlight.
         Subtree-scoped Highlight lives inside the Subtree submenu. -->
    @if (showHighlight(cn) || showRemoveHighlight(cn)) {
      <mat-divider />
    }
    @if (showHighlight(cn)) {
      <button
        mat-menu-item
        type="button"
        [matMenuTriggerFor]="highlightMenu"
        (click)="onHighlightItemClick($event, cn, false)"
        (menuOpened)="onSwatchMenuOpened('single')"
      >
        <jj-icon name="bookmark" [size]="18" />
        <span>{{ ctxHighlightLabel }}</span>
      </button>
    }
    @if (showRemoveHighlight(cn)) {
      <button mat-menu-item type="button" (click)="removeManualHighlight(cn)">
        <jj-icon name="trash" [size]="18" />
        <span>{{ ctxRemoveHighlightLabel }}</span>
      </button>
    }
    <!-- Find section -->
    @if (showFindByKey(cn) || showFindByValue(cn)) {
      <mat-divider />
    }
    @if (showFindByKey(cn)) {
      <button mat-menu-item type="button" (click)="findByKey(cn)">
        <jj-icon name="find-by-key" [size]="18" />
        <span>{{ ctxFindByKeyLabel }}</span>
      </button>
    }
    @if (showFindByValue(cn)) {
      <button mat-menu-item type="button" (click)="findByValue(cn)">
        <jj-icon name="find-by-value" [size]="18" />
        <span>{{ ctxFindByValueLabel }}</span>
      </button>
    }
    <!-- Reshape section: surfaced default-shortcut + new top-level
         `Expand all from here` row (v0.23.0) + Subtree submenu.
         The surfaced row carries the .ctx-default-action class only
         here (not at the top Copy value row) when the dblclick action
         is collapse / expand-1-level. For primitives and empty
         containers the bolding lives on Copy value at the top. The
         new `Expand all from here` row is non-bolded so the dblclick
         mirror remains visually unique (DESIGN_SPEC.md §Tree View
         Panel -- dblclick-mirror mandate). It is gated on
         `hasContainerDescendants(cn)` so primitives-only containers
         (where `expandAll` produces the same end state as the bolded
         `Expand 1 level`) fall through to the surfaced shortcut alone. -->
    @if (
      surfacedShortcutLabel() ||
      (showExpandAllFromHere(cn) && hasContainerDescendants(cn)) ||
      showSubtreeMenu(cn)
    ) {
      <mat-divider />
    }
    @if (surfacedShortcutLabel(); as label) {
      <button
        mat-menu-item
        type="button"
        class="ctx-default-action"
        (click)="onSurfacedShortcutClick(cn)"
      >
        @if (surfacedShortcutIconName(); as iconName) {
          <jj-icon [name]="iconName" [size]="18" />
        }
        <span>{{ label }}</span>
        <span class="sr-only">{{ defaultActionA11yHint }}</span>
      </button>
    }
    @if (showExpandAllFromHere(cn) && hasContainerDescendants(cn)) {
      <button mat-menu-item type="button" (click)="expandAllFromHere(cn, 'topRow')">
        <jj-icon name="expand-subtree" [size]="18" />
        <span>{{ ctxExpandAllFromHereLabelFor(cn) }}</span>
      </button>
    }
    @if (showSubtreeMenu(cn)) {
      <button
        mat-menu-item
        type="button"
        [matMenuTriggerFor]="subtreeMenu"
        (menuOpened)="onSubtreeMenuOpened()"
      >
        <jj-icon name="subtree" [size]="18" />
        <span>{{ ctxSubtreeMenuLabel }}</span>
      </button>
    } @else if (subtreeElevatedAction(cn); as elevated) {
      <!--
        Single-item Subtree elevation (v0.19.4): when the Subtree
        submenu would only contain one visible action, that action
        is rendered directly in the row menu instead of nested
        behind a `Subtree >` flyout. Eliminates the "open submenu
        with only one option" UX. The `'collapseSame'` sentinel
        suppresses entirely (the action is already on the surfaced
        default-shortcut row). v0.23.0 retired the `'expandSame'`
        sentinel: the equivalent suppression for the lone-+1 Expand
        case is now handled upstream by
        `isLoneDepth1RedundantWithSurfaced`.
      -->
      @switch (elevated.kind) {
        @case ('highlightTree') {
          <button
            mat-menu-item
            type="button"
            [matMenuTriggerFor]="highlightTreeMenu"
            (click)="onHighlightItemClick($event, cn, true)"
            (menuOpened)="onSwatchMenuOpened('cascade')"
          >
            <jj-icon name="bookmark" [size]="18" />
            <span>{{ ctxHighlightTreeElevatedLabel }}</span>
          </button>
        }
        @case ('removeTreeHighlight') {
          <button
            mat-menu-item
            type="button"
            [attr.aria-label]="removeTreeHighlightAriaLabel(cn)"
            (click)="removeManualTreeHighlight(cn)"
          >
            <jj-icon name="trash" [size]="18" />
            <span>{{ ctxRemoveTreeHighlightElevatedLabel }}</span>
          </button>
        }
        @case ('collapse') {
          <button mat-menu-item type="button" (click)="collapseFromHere(cn)">
            <jj-icon name="collapse-subtree" [size]="18" />
            <span>{{ ctxCollapseFromHereLabel }}</span>
          </button>
        }
        @case ('isolate') {
          <button mat-menu-item type="button" (click)="isolateRow(cn, 'single')">
            <jj-icon name="isolate" [size]="18" />
            <span>{{ ctxIsolateLabel }}</span>
          </button>
        }
        @case ('expandSingle') {
          <button
            mat-menu-item
            type="button"
            (click)="onExpandSingleElevatedClick(cn, elevated.single)"
          >
            <jj-icon name="expand-subtree" [size]="18" />
            <span>{{ expandSingleElevatedLabel(elevated.single) }}</span>
          </button>
        }
        @case ('expandSubmenu') {
          <button mat-menu-item type="button" [matMenuTriggerFor]="expandFromHereMenu">
            <jj-icon name="expand-subtree" [size]="18" />
            <span>{{ ctxExpandFromHereElevatedMenuLabel }}</span>
          </button>
        }
      }
    }
  }
</mat-menu>

<!--
  Subtree submenu: contains every subtree-affecting action. Items
  inside drop the "from here" / "subtree" suffix because the submenu
  name carries the scope. Spec terms (Isolate, Collapse siblings)
  preserved per DESIGN_SPEC.md §514.
-->
<mat-menu #subtreeMenu="matMenu" class="jj-menu tree-row-menu">
  @if (contextNode(); as cn) {
    @if (showHighlightTree(cn)) {
      <button
        mat-menu-item
        type="button"
        [matMenuTriggerFor]="highlightTreeMenu"
        (click)="onHighlightItemClick($event, cn, true)"
        (menuOpened)="onSwatchMenuOpened('cascade')"
      >
        <jj-icon name="bookmark" [size]="18" />
        <span>{{ ctxHighlightTreeLabel }}</span>
      </button>
    }
    @if (showRemoveTreeHighlight(cn)) {
      <button
        mat-menu-item
        type="button"
        [attr.aria-label]="removeTreeHighlightAriaLabel(cn)"
        (click)="removeManualTreeHighlight(cn)"
      >
        <jj-icon name="trash" [size]="18" />
        <span>{{ ctxRemoveTreeHighlightLabel }}</span>
      </button>
    }
    @if (
      (showHighlightTree(cn) || showRemoveTreeHighlight(cn)) &&
      (showCollapse(cn) || showIsolateSingle(cn) || showIsolatePair(cn))
    ) {
      <mat-divider />
    }
    @if (showCollapse(cn)) {
      <button mat-menu-item type="button" (click)="collapseFromHere(cn)">
        <jj-icon name="collapse-subtree" [size]="18" />
        <span>{{ ctxSubtreeCollapseLabel }}</span>
      </button>
    }
    @if (showIsolateSingle(cn)) {
      <button mat-menu-item type="button" (click)="isolateRow(cn, 'single')">
        <jj-icon name="isolate" [size]="18" />
        <span>{{ ctxIsolateLabel }}</span>
      </button>
    }
    @if (showIsolatePair(cn)) {
      <button mat-menu-item type="button" (click)="collapseSiblings(cn)">
        <jj-icon name="collapse-siblings" [size]="18" />
        <span>{{ ctxCollapseSiblingsLabel }}</span>
      </button>
      <button mat-menu-item type="button" (click)="isolateRow(cn, 'wide')">
        <jj-icon name="isolate" [size]="18" />
        <span>{{ ctxIsolateLabel }}</span>
      </button>
    }
    @if (
      (showCollapse(cn) || showIsolateSingle(cn) || showIsolatePair(cn)) &&
      showExpandFromHereMenu(cn)
    ) {
      <mat-divider />
    }
    @if (showExpandFromHereSubmenu(cn)) {
      <button mat-menu-item type="button" [matMenuTriggerFor]="expandFromHereMenu">
        <jj-icon name="expand-subtree" [size]="18" />
        <span>{{ ctxSubtreeExpandMenuLabel }}</span>
      </button>
    } @else if (expandFromHereSingleAction(cn); as single) {
      <!--
        Single-item Expand elevation inside Subtree (v0.19.4):
        when the Expand sub-submenu would only contain one item,
        that item renders inline within Subtree instead of nested
        behind another `Expand >` flyout. Label uses the elevated
        form (e.g. "Expand 2 levels from here") because the
        submenu's scope is no longer carried by an "Expand" name.
        v0.23.0: this elevation no longer fires for `expandAll`
        (the new top-level `Expand all from here` row covers it
        directly) nor for the lone-+1 case under `expandRow`
        defaults (suppressed by
        `isLoneDepth1RedundantWithSurfaced` so the bolded
        surfaced shortcut is not duplicated). Reaches here only
        for lone-+N depth-only cases with N>=2 OR N=1 under
        `collapseRow` defaults (rare; e.g., expanded outer +
        collapsed deep descendant).
      -->
      <button mat-menu-item type="button" (click)="onExpandSingleElevatedClick(cn, single)">
        <jj-icon name="expand-subtree" [size]="18" />
        <span>{{ expandSingleElevatedLabel(single) }}</span>
      </button>
    }
  }
</mat-menu>

<!--
  Expand-from-here sub-submenu (inside Subtree). Per DESIGN_SPEC.md
  §516, depth labels carry "+N" prefix to distinguish relative
  expand-only semantics from the toolbar's absolute "Expand to Level"
  dropdown. v0.23.0 retired the leading `All` leaf -- the new
  top-level `Expand all from here` row in the row menu covers the
  same action with one fewer click. The depth range extended from
  `+1..+5` to `+1..+9` to mirror the toolbar's `Expand to Level`
  range. `showExpandToDepth` hides entries beyond the subtree's
  reachable container depth, so `+6..+9` only render for subtrees
  deep enough to need them.
-->
<mat-menu #expandFromHereMenu="matMenu" class="jj-menu tree-row-menu">
  @if (contextNode(); as cn) {
    @if (showExpandToDepth(cn, 1)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 1)">
        {{ ctxExpandToDepth1Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 2)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 2)">
        {{ ctxExpandToDepth2Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 3)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 3)">
        {{ ctxExpandToDepth3Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 4)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 4)">
        {{ ctxExpandToDepth4Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 5)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 5)">
        {{ ctxExpandToDepth5Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 6)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 6)">
        {{ ctxExpandToDepth6Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 7)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 7)">
        {{ ctxExpandToDepth7Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 8)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 8)">
        {{ ctxExpandToDepth8Label }}
      </button>
    }
    @if (showExpandToDepth(cn, 9)) {
      <button mat-menu-item type="button" (click)="expandToDepthFromHere(cn, 9)">
        {{ ctxExpandToDepth9Label }}
      </button>
    }
  }
</mat-menu>

<mat-menu #highlightMenu="matMenu" class="jj-menu tree-highlight-menu">
  @if (contextNode(); as cn) {
    <jj-highlight-flyout
      #singleFlyout
      [cascade]="false"
      (apply)="onHighlightFlyoutApply(cn, false, $event)"
      (close)="onHighlightFlyoutClose(cn)"
    />
  }
</mat-menu>

<mat-menu #highlightTreeMenu="matMenu" class="jj-menu tree-highlight-menu">
  @if (contextNode(); as cn) {
    <jj-highlight-flyout
      #cascadeFlyout
      [cascade]="true"
      (apply)="onHighlightFlyoutApply(cn, true, $event)"
      (close)="onHighlightFlyoutClose(cn)"
    />
  }
</mat-menu>
